import { useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { errorHandleMiddleware, processFile } from '@entities/attachment/lib';
import { TAttachmentDTO, TAttachmentOnClient } from '@entities/attachment/model/types';
import { generateId } from '@shared/helpers';

import { IJournalEventsFilesUploaderBase } from '../../model/types';

export const useJournalEventsFilesUploader = ({
    attachments,
    setAttachments,
}: IJournalEventsFilesUploaderBase) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const onAddFiles = async (files: File[]) => {
        const tempAttachments: TAttachmentOnClient[] = files.map((file) => ({
            id: generateId(true) as number,
            file,
            isLoading: true,
        }));

        const updatedWithTemp = [...attachments, ...tempAttachments];
        setAttachments(updatedWithTemp);

        const processedFiles = await Promise.all(
            files.map((file) => {
                const temp = tempAttachments.find((ta) => ta.file.name === file.name);
                const fileId = temp?.id ?? (generateId(true) as number);
                return processFile(file, fileId);
            })
        );

        const finalAttachments = updatedWithTemp.map((attachment) => {
            const matched = processedFiles.find((pf) => pf.id === attachment.id);
            return matched || attachment;
        });

        setAttachments(finalAttachments);
    };

    const onClickInputRef = () => {
        fileInputRef.current?.click();
    };

    const uploadFilesHandler = errorHandleMiddleware({
        onAddFiles,
        alreadyAttachedLength: attachments.length || 0,
        t,
        maxAttachmentSizeInMb: 1000,
        maxOverallSizeInMb: 10000,
    });

    const onDeleteAttachment = (deletedId: TAttachmentOnClient['id'] | TAttachmentDTO['id']) => {
        const filtered = attachments.filter((attachment) => attachment.id !== deletedId);
        setAttachments(filtered);
    };

    return {
        fileInputRef,
        onUploadHandler: uploadFilesHandler,
        onDeleteAttachment,
        t,
        onClickInputRef,
    };
};
